import { create } from "zustand";

/**
 * docGenerationStore — tracks an in-flight document authoring run that needs
 * NO source confirmation (Jun 22, 2026).
 *
 * A document create/fork/edit with an unambiguous source goes straight to
 * ~40s of LLM authoring before the ``action_proposal`` + ``doc_diff_payload``
 * arrive. With no source-confirmation card to carry the "executing" state, the
 * work-area looked dead in that window. The BE now fires an early
 * ``doc_generation_started`` WS event the instant authoring begins; the bridge
 * sets ``active`` here, the work-area shows the same ``GeneratingState`` loader
 * the source-confirmation path shows, and ``useActionRouter`` clears it the
 * moment the proposal lands (or a safety timeout fires).
 *
 * Deliberately a tiny boolean+title slice (not folded into
 * ``activeActionStore``): the signal exists BEFORE any action proposal does, so
 * there's nothing to attach it to there yet.
 */
interface DocGenerationState {
  /** True while a no-confirmation document authoring run is in flight. */
  active: boolean;
  /** Best-effort title for the loader copy (e.g. the new doc name). */
  title: string | null;
  begin: (title: string | null) => void;
  clear: () => void;
}

export const useDocGenerationStore = create<DocGenerationState>()((set) => ({
  active: false,
  title: null,
  begin: (title) => set({ active: true, title }),
  clear: () => set({ active: false, title: null }),
}));

export const selectDocGenerationActive = (s: DocGenerationState): boolean =>
  s.active;
